// src/components/GenerateObjectReplacerModal.jsx
import React, { useState, useRef, useEffect, useCallback, Fragment } from "react";
import { Dialog } from "@headlessui/react";
import {
  X,
  RefreshCcw,
  Loader2,
  AlertCircle,
  Brush,
  Eraser,
  Plus,
  Trash2,
} from "lucide-react";
import axios from "axios";
import { useAuth } from "@clerk/clerk-react";

const BRAND_PINK = "#FC2783";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  "https://ecom-ads-ai-backend-production.up.railway.app";

// Internal mask-hardening (no UI): small feather + dilation in natural pixels
const INTERNAL_FEATHER_PX = 1; // softens jaggies before threshold
const INTERNAL_EXPAND_PX = 3; // expands mask slightly to fully cover edges/shadows

const steps = [
  { number: 1, label: "Select Asset" },
  { number: 2, label: "Area to Replace" },
  { number: 3, label: "Prompt" },
];

function StepperHeader({ step }) {
  return (
    <div className="flex items-center gap-2 px-8 pt-7 pb-2">
      {steps.map((s, idx) => (
        <Fragment key={s.number}>
          <span
            className={`rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold ${
              step === s.number
                ? "bg-pink-600 text-white"
                : "bg-[#29294a] text-gray-400"
            }`}
          >
            {s.number}
          </span>
          <span
            className={`text-base sm:text-lg font-semibold ${
              step === s.number ? "text-white" : "text-gray-400"
            } ${idx !== steps.length - 1 ? "mr-8" : ""}`}
          >
            {s.label}
          </span>
        </Fragment>
      ))}
    </div>
  );
}

function StepFooter({
  step,
  onBack,
  onNext,
  onGenerate,
  canNext,
  canGenerate,
  generating,
}) {
  return (
    <div className="absolute bottom-0 left-0 w-full bg-[#191930] border-t border-[#23233a] flex justify-between items-center px-8 py-4 z-20">
      <button
        className="px-7 py-2 rounded-xl font-bold bg-[#222246] hover:bg-[#2B2B4A] text-white disabled:opacity-40"
        disabled={step === 1 || generating}
        onClick={onBack}
        type="button"
      >
        Back
      </button>

      {step < 3 ? (
        <button
          className="px-7 py-2 bg-pink-600 hover:bg-pink-700 rounded-xl font-bold text-white disabled:opacity-40"
          onClick={onNext}
          disabled={!canNext || generating}
          type="button"
        >
          Next
        </button>
      ) : (
        <button
          className="px-7 py-2 bg-pink-600 hover:bg-pink-700 rounded-xl font-bold text-white disabled:opacity-40 flex items-center"
          onClick={onGenerate}
          disabled={!canGenerate || generating}
          type="button"
        >
          {generating && <Loader2 className="mr-2 animate-spin" size={18} />}
          {generating ? "Generating…" : "Generate"}
        </button>
      )}
    </div>
  );
}

export default function GenerateObjectReplacerModal({
  productImages = [],
  onClose,
  onGenerate,
}) {
  const { getToken } = useAuth();

  const [step, setStep] = useState(1);
  const [selectedImage, setSelectedImage] = useState(null);

  // ✅ Step 1 manual upload (same flow + same endpoint as Ad Marketing Video Step 1)
  const [step1UploadedAsset, setStep1UploadedAsset] = useState(null); // { id, url } | null
  const [step1UploadLoading, setStep1UploadLoading] = useState(false);
  const step1FileInputRef = useRef(null);

  /** Step 2: paint mask (canvas) */
  const canvasRef = useRef(null);
  const imgRef = useRef(null);

  const [brushSize, setBrushSize] = useState(28);
  const [eraseMode, setEraseMode] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasPaint, setHasPaint] = useState(false);
  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });

  // Cursor ring (pink, follows pointer & matches brush size)
  const [cursor, setCursor] = useState({ x: 0, y: 0, visible: false });

  // Fit selected image to canvas area when entering step 2
  useEffect(() => {
    if (step !== 2 || !selectedImage) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const natW = img.naturalWidth || img.width;
      const natH = img.naturalHeight || img.height;
      setNaturalSize({ w: natW, h: natH });

      const maxW = 820;
      const maxH = 520;
      let w = natW;
      let h = natH;
      const r = Math.min(maxW / w, maxH / h, 1);
      w = Math.max(1, Math.round(w * r));
      h = Math.max(1, Math.round(h * r));

      canvas.width = w;
      canvas.height = h;
      ctx.clearRect(0, 0, w, h); // transparent base
      setHasPaint(false);

      if (imgRef.current) {
        imgRef.current.src = selectedImage;
        imgRef.current.width = w;
        imgRef.current.height = h;
      }
    };
    img.src = selectedImage;
  }, [step, selectedImage]);

  const handlePointer = (e, type) => {
    if (step !== 2) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const getXY = (ev) => {
      const cx = (ev.clientX ?? ev.touches?.[0]?.clientX) - rect.left;
      const cy = (ev.clientY ?? ev.touches?.[0]?.clientY) - rect.top;
      return [cx, cy];
    };

    const ctx = canvas.getContext("2d");
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.lineWidth = brushSize;

    if (type === "down") {
      setIsDrawing(true);
      const [x, y] = getXY(e);
      setCursor({ x, y, visible: true });
      ctx.globalCompositeOperation = eraseMode
        ? "destination-out"
        : "source-over";
      ctx.strokeStyle = eraseMode
        ? "rgba(0,0,0,1)"
        : "rgba(255,255,255,1)";
      ctx.beginPath();
      ctx.moveTo(x, y);
      // tiny stroke so a tap registers as paint
      ctx.lineTo(x + 0.01, y + 0.01);
      ctx.stroke();
      setHasPaint(true);
    } else if (type === "move") {
      const [x, y] = getXY(e);
      setCursor({ x, y, visible: true });
      if (isDrawing) {
        ctx.globalCompositeOperation = eraseMode
          ? "destination-out"
          : "source-over";
        ctx.strokeStyle = eraseMode
          ? "rgba(0,0,0,1)"
          : "rgba(255,255,255,1)";
        ctx.lineTo(x, y);
        ctx.stroke();
        setHasPaint(true);
      }
    } else if (type === "up") {
      setIsDrawing(false);
    } else if (type === "enter") {
      setCursor((c) => ({ ...c, visible: true }));
    } else if (type === "leave") {
      setCursor((c) => ({ ...c, visible: false }));
      setIsDrawing(false);
    } else if (type === "touchstart" || type === "touchmove") {
      // Hide ring on touch devices
      setCursor((c) => ({ ...c, visible: false }));
    } else if (type === "touchend") {
      setIsDrawing(false);
    }
  };

  const clearPaint = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height); // reset to transparent
    setHasPaint(false);
  };

  /** Step 3: prompt + AI helper */
  const [prompt, setPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");
  const [suggestRequested, setSuggestRequested] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [paintPreviewUrl, setPaintPreviewUrl] = useState("");

  // ✅ Step 1 manual upload handler (same behavior as Ad Marketing Video Step 1)
  const handleStep1Upload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setStep1UploadLoading(true);
    setAiError("");

    try {
      if (file.size > 20 * 1024 * 1024) {
        setAiError("Image is too large. Please upload a file under 20MB.");
        return;
      }

      const token = await getToken();
      const formData = new FormData();
      formData.append("file", file);

      // Reuse existing upload endpoint (same as Ad Marketing Video end card upload)
      const { data } = await axios.post(
        `${BACKEND_URL}/api/videos/marketing/endcard/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const url = data?.url;
      if (url) {
        const newAsset = { id: `asset-upload-${Date.now()}`, url };
        setStep1UploadedAsset(newAsset);
        setSelectedImage(url);
      }
    } catch {
      setAiError("Failed to upload image.");
    } finally {
      setStep1UploadLoading(false);
      event.target.value = "";
    }
  };

  // Export mask as white on TRANSPARENT (alpha), hardened to avoid ghost edges.
  const buildPaintPreview = useCallback(() => {
    if (!hasPaint || !canvasRef.current) {
      setPaintPreviewUrl("");
      return "";
    }

    const viewCanvas = canvasRef.current;

    // Upscale to original image dimensions so Phot.AI mask aligns perfectly
    const upscaled = document.createElement("canvas");
    const targetW = naturalSize.w || viewCanvas.width;
    const targetH = naturalSize.h || viewCanvas.height;
    upscaled.width = Math.max(1, targetW);
    upscaled.height = Math.max(1, targetH);
    const uctx = upscaled.getContext("2d");

    // Start clean (fully transparent)
    uctx.clearRect(0, 0, upscaled.width, upscaled.height);
    uctx.imageSmoothingEnabled = true;

    // Draw the painted strokes to natural resolution
    uctx.drawImage(
      viewCanvas,
      0,
      0,
      viewCanvas.width,
      viewCanvas.height,
      0,
      0,
      upscaled.width,
      upscaled.height
    );

    // Optional very light blur (feather) to soften stairsteps BEFORE threshold
    if (INTERNAL_FEATHER_PX > 0) {
      const tmp = document.createElement("canvas");
      tmp.width = upscaled.width;
      tmp.height = upscaled.height;
      const tctx = tmp.getContext("2d");
      tctx.drawImage(upscaled, 0, 0);

      const passes = Math.min(3, INTERNAL_FEATHER_PX);
      for (let p = 0; p < passes; p++) {
        // cheap, directional blur-ish passes
        for (let dx = -1; dx <= 1; dx += 1) {
          if (dx !== 0) tctx.drawImage(tmp, dx, 0);
        }
        for (let dy = -1; dy <= 1; dy += 1) {
          if (dy !== 0) tctx.drawImage(tmp, 0, dy);
        }
      }

      uctx.clearRect(0, 0, upscaled.width, upscaled.height);
      uctx.drawImage(tmp, 0, 0);
    }

    // Binarize alpha → white where painted, fully transparent elsewhere
    const id = uctx.getImageData(0, 0, upscaled.width, upscaled.height);
    const d = id.data;
    for (let i = 0; i < d.length; i += 4) {
      const a = d[i + 3];
      if (a > 16) {
        d[i] = 255;
        d[i + 1] = 255;
        d[i + 2] = 255;
        d[i + 3] = 255;
      } else {
        d[i] = 0;
        d[i + 1] = 0;
        d[i + 2] = 0;
        d[i + 3] = 0;
      }
    }
    uctx.putImageData(id, 0, 0);

    // Small dilation to cover tiny edges/shadows so nothing is left behind
    if (INTERNAL_EXPAND_PX > 0) {
      const src = uctx.getImageData(0, 0, upscaled.width, upscaled.height);
      const out = uctx.createImageData(upscaled.width, upscaled.height);
      out.data.set(src.data);

      const sw = upscaled.width;
      const sh = upscaled.height;
      const r = Math.max(1, Math.min(12, INTERNAL_EXPAND_PX));

      for (let y = 0; y < sh; y += 1) {
        for (let x = 0; x < sw; x += 1) {
          const idx = (y * sw + x) * 4;
          if (src.data[idx + 3] === 255) {
            const x0 = Math.max(0, x - r);
            const x1 = Math.min(sw - 1, x + r);
            const y0 = Math.max(0, y - r);
            const y1 = Math.min(sh - 1, y + r);
            for (let yy = y0; yy <= y1; yy += 1) {
              let base = (yy * sw + x0) * 4;
              for (let xx = x0; xx <= x1; xx += 1) {
                out.data[base] = 255;
                out.data[base + 1] = 255;
                out.data[base + 2] = 255;
                out.data[base + 3] = 255;
                base += 4;
              }
            }
          }
        }
      }
      uctx.putImageData(out, 0, 0);
    }

    const dataUrl = upscaled.toDataURL("image/png"); // preserve alpha
    setPaintPreviewUrl(dataUrl);
    return dataUrl;
  }, [hasPaint, naturalSize.w, naturalSize.h]);

  const regeneratePrompt = useCallback(async () => {
    const mp = paintPreviewUrl || buildPaintPreview();
    if (!selectedImage || !mp) return;

    try {
      setAiLoading(true);
      setAiError("");
      const token = await getToken();
      const res = await axios.post(
        `${BACKEND_URL}/api/object-replacer/prompt`,
        { productImageUrl: selectedImage, maskImageUrl: mp },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const suggestion = res.data?.prompt?.trim();
      if (suggestion) setPrompt(suggestion);
    } catch {
      setAiError("AI failed to generate a prompt. You can type your own.");
    } finally {
      setAiLoading(false);
    }
  }, [paintPreviewUrl, buildPaintPreview, selectedImage, getToken]);

  // Auto-fetch AI suggestion on entering step 3 (robust)
  useEffect(() => {
    const autoSuggest = async () => {
      if (step !== 3 || !selectedImage || !hasPaint || suggestRequested) return;

      const mp = paintPreviewUrl || buildPaintPreview();
      if (!mp) return; // empty/0x0 guard

      try {
        setSuggestRequested(true);
        setAiLoading(true);
        setAiError("");
        const token = await getToken();
        const res = await axios.post(
          `${BACKEND_URL}/api/object-replacer/prompt`,
          { productImageUrl: selectedImage, maskImageUrl: mp },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const suggestion = res.data?.prompt?.trim();
        if (suggestion) setPrompt(suggestion);
      } catch {
        setAiError("AI failed to generate a prompt. You can type your own.");
      } finally {
        setAiLoading(false);
      }
    };

    autoSuggest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    step,
    selectedImage,
    hasPaint,
    paintPreviewUrl,
    buildPaintPreview,
    getToken,
    suggestRequested,
  ]);

  /** Navigation */
  const onBack = () => setStep((s) => Math.max(1, s - 1));
  const onNext = () => {
    if (step === 1 && !selectedImage) return;
    if (step === 2) {
      // Build/refresh preview so step 3 can immediately use it
      buildPaintPreview();
      setSuggestRequested(false);
    }
    setStep((s) => Math.min(3, s + 1));
  };

  /** Generate (spinner on button; modal closes automatically on success) */
  const handleGenerate = async () => {
    if (!selectedImage || !hasPaint) {
      // eslint-disable-next-line no-alert
      alert("Please paint the area to replace in Step 2.");
      return;
    }

    const dataUrl = paintPreviewUrl || buildPaintPreview();
    if (!dataUrl) {
      // eslint-disable-next-line no-alert
      alert(
        "Your mask looks empty. Please paint the area to replace in Step 2, then try again."
      );
      return;
    }
    if (!prompt.trim()) {
      // eslint-disable-next-line no-alert
      alert("Please add a prompt describing what should replace the masked area.");
      return;
    }

    const res = await fetch(dataUrl);
    const blob = await res.blob();
    const maskFile = new File([blob], "painted-mask.png", { type: "image/png" });

    setGenerating(true);
    try {
      await onGenerate(selectedImage, maskFile, prompt.trim());
      onClose(); // ✅ close only on success
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Object Replacer generate failed:", err);
      // eslint-disable-next-line no-alert
      alert(
        typeof err?.message === "string"
          ? `Generate failed: ${err.message}`
          : "Generate failed. Please try again."
      );
    } finally {
      setGenerating(false);
    }
  };

  const canNext = step === 1 ? !!selectedImage : step === 2 ? hasPaint : true;
  const canGenerate = !!selectedImage && hasPaint && !!prompt.trim();

  // Cursor ring style (pink, sized to brush)
  const ringStyle = {
    position: "absolute",
    left: cursor.x,
    top: cursor.y,
    width: brushSize,
    height: brushSize,
    transform: "translate(-50%, -50%)",
    pointerEvents: "none",
    borderRadius: "9999px",
    border: `2px solid ${BRAND_PINK}`,
    backgroundColor: "rgba(252,39,131,0.08)",
    boxShadow:
      "0 0 0 1px rgba(252,39,131,0.35), 0 0 12px rgba(252,39,131,0.2)",
    zIndex: 5,
  };

  return (
    <Dialog
      open
      onClose={generating ? () => {} : onClose}
      className="fixed inset-0 z-50 flex items-center justify-center"
    >
      <div className="fixed inset-0 bg-black/60" aria-hidden="true" />

      <div className="relative bg-[#191930] text-white rounded-2xl shadow-2xl w-full max-w-[980px] min-h-[560px] h-[86vh] flex flex-col overflow-hidden">
        {/* Close */}
        <button
          onClick={onClose}
          aria-label="Close modal"
          className="absolute top-4 right-4 bg-white text-black p-2 rounded-full hover:bg-gray-100 z-10 disabled:opacity-40"
          disabled={generating}
          type="button"
        >
          <X size={22} />
        </button>

        <StepperHeader step={step} />

        {/* Scroll area (same behavior as Ad Marketing Video modal) */}
        <div className="flex-1 flex flex-col px-8 pt-2 pb-32 min-h-0 overflow-y-auto">
          {/* Step 1: Select Asset (✅ manual upload tile added, same flow as Ad Marketing Video) */}
          {step === 1 && (
            <>
              <p className="mb-4 text-gray-300 flex items-center gap-4">
                Select one product image to edit.
                <span className="text-gray-400 text-sm">
                  Selected {selectedImage ? 1 : 0} / 1
                </span>
              </p>

              {aiError && (
                <div className="mb-3 text-red-500 font-semibold">{aiError}</div>
              )}

              <input
                ref={step1FileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleStep1Upload}
              />

              <div className="rounded-lg border border-[#23233a] bg-[#191930] px-4 py-3">
                <div className="grid grid-cols-5 gap-4">
                  {/* ✅ Upload tile (same UX as Ad Marketing Video Step 1) */}
                  <div
                    className={`relative rounded-lg overflow-hidden border-2 group transition ${
                      step1UploadedAsset
                        ? selectedImage === step1UploadedAsset.url
                          ? "border-pink-500 ring-2 ring-pink-600 cursor-pointer"
                          : "border-transparent hover:border-pink-400 cursor-pointer"
                        : step1UploadLoading || generating
                        ? "border-[#2B2B4A] opacity-60 cursor-not-allowed"
                        : "border-[#3A3A4A] border-dashed hover:border-pink-400 cursor-pointer"
                    }`}
                    onClick={() => {
                      if (generating || step1UploadLoading) return;

                      if (step1UploadedAsset) {
                        setSelectedImage(step1UploadedAsset.url);
                        return;
                      }

                      step1FileInputRef.current?.click();
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (generating || step1UploadLoading) return;

                        if (step1UploadedAsset) {
                          setSelectedImage(step1UploadedAsset.url);
                          return;
                        }

                        step1FileInputRef.current?.click();
                      }
                    }}
                    aria-label={step1UploadedAsset ? "Uploaded asset" : "Upload image"}
                  >
                    <div className="aspect-[4/5] bg-black relative">
                      {step1UploadedAsset ? (
                        <img
                          className="object-cover w-full h-full"
                          src={step1UploadedAsset.url}
                          alt="Uploaded asset"
                          loading="lazy"
                          draggable={false}
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center bg-[#151529]">
                          <div className="w-12 h-12 rounded-full bg-[#23233a] border border-[#2B2B4A] flex items-center justify-center mb-3">
                            <Plus size={22} className="text-white" />
                          </div>
                          <div className="text-white font-semibold text-sm">
                            Upload image
                          </div>
                          <div className="text-gray-400 text-[11px] mt-1 px-3 text-center">
                            JPG, PNG, WEBP
                          </div>
                        </div>
                      )}

                      {step1UploadLoading && (
                        <div className="absolute inset-0 bg-black/55 flex items-center justify-center">
                          <Loader2 className="animate-spin text-white" />
                        </div>
                      )}

                      <div className="absolute inset-0 bg-black/10 group-hover:bg-pink-600/10 transition" />
                    </div>

                    {/* Actions (only when uploaded) */}
                    {step1UploadedAsset && (
                      <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition">
                        <button
                          type="button"
                          className="px-2.5 py-1.5 rounded-md bg-black/70 hover:bg-black/85 text-white text-xs font-semibold border border-white/10"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (generating || step1UploadLoading) return;
                            step1FileInputRef.current?.click();
                          }}
                          aria-label="Replace uploaded image"
                          disabled={generating || step1UploadLoading}
                        >
                          Replace
                        </button>

                        <button
                          type="button"
                          className="p-2 rounded-md bg-black/70 hover:bg-black/85 text-white border border-white/10"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (generating) return;

                            const prevUrl = step1UploadedAsset.url;
                            setStep1UploadedAsset(null);
                            if (selectedImage === prevUrl) setSelectedImage(null);
                          }}
                          aria-label="Remove uploaded image"
                          disabled={generating}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Existing product images */}
                  {productImages.map((img, i) => {
                    const selected = selectedImage === img;
                    return (
                      <button
                        key={`${i}-${img}`}
                        type="button"
                        className={`relative rounded-lg overflow-hidden border-2 cursor-pointer group transition ${
                          selected
                            ? "border-pink-500 ring-2 ring-pink-600"
                            : "border-transparent hover:border-pink-400"
                        }`}
                        onClick={() => setSelectedImage(img)}
                        title="Select image"
                      >
                        <div className="aspect-[4/5] bg-black">
                          <img
                            src={img}
                            alt="Product"
                            className="object-cover w-full h-full"
                            loading="lazy"
                            draggable={false}
                          />
                        </div>
                        <div className="absolute inset-0 bg-black/10 group-hover:bg-pink-600/10 transition" />
                      </button>
                    );
                  })}
                </div>

                {productImages.length === 0 && !step1UploadedAsset && (
                  <div className="text-white/70 text-center py-8 select-none">
                    No product images found. You can upload an image above to continue.
                  </div>
                )}
              </div>
            </>
          )}

          {/* Step 2: Area to Replace (paint mask) */}
          {step === 2 && (
            <div className="grid grid-cols-1 lg:grid-cols-[auto_280px] gap-6">
              {/* Preview + Canvas */}
              <div className="relative w-full">
                <h2 className="text-xl font-bold text-white mb-3">Area to Replace</h2>
                <div
                  className={`relative inline-block rounded-xl overflow-hidden ${
                    cursor.visible ? "cursor-none" : "cursor-crosshair"
                  }`}
                  style={{ background: "#0f0f22" }}
                >
                  {/* Source image beneath */}
                  <img ref={imgRef} alt="Preview" />
                  {/* Canvas (white = replace; exported as white on transparent) */}
                  <canvas
                    ref={canvasRef}
                    className="absolute top-0 left-0"
                    onMouseEnter={(e) => handlePointer(e, "enter")}
                    onMouseLeave={(e) => handlePointer(e, "leave")}
                    onMouseDown={(e) => handlePointer(e, "down")}
                    onMouseMove={(e) => handlePointer(e, "move")}
                    onMouseUp={(e) => handlePointer(e, "up")}
                    onTouchStart={(e) => handlePointer(e, "touchstart")}
                    onTouchMove={(e) => handlePointer(e, "touchmove")}
                    onTouchEnd={(e) => handlePointer(e, "touchend")}
                  />
                  {/* Brush cursor ring */}
                  {cursor.visible && <div style={ringStyle} />}
                </div>
                <p className="text-sm text-gray-400 mt-2">
                  Paint <b>white</b> over the area you want to <b>replace</b>. Use Eraser to undo
                  (transparent elsewhere).
                </p>
              </div>

              {/* Tools — same layout as Remover modal */}
              <div className="bg-[#1C1C2E] border border-[#2A2A40] rounded-2xl p-4 h-fit">
                <p className="font-semibold mb-3">Brush</p>
                <div className="flex items-center gap-3 mb-4">
                  <button
                    type="button"
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${
                      !eraseMode
                        ? "border-pink-500 bg-[#2A2A40]"
                        : "border-transparent bg-[#1F1F2F]"
                    }`}
                    onClick={() => setEraseMode(false)}
                  >
                    <Brush size={18} /> Paint
                  </button>
                  <button
                    type="button"
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${
                      eraseMode
                        ? "border-pink-500 bg-[#2A2A40]"
                        : "border-transparent bg-[#1F1F2F]"
                    }`}
                    onClick={() => setEraseMode(true)}
                  >
                    <Eraser size={18} /> Erase
                  </button>
                </div>

                <label className="text-sm text-gray-300">Brush size</label>
                <input
                  type="range"
                  min={6}
                  max={80}
                  value={brushSize}
                  onChange={(e) => setBrushSize(parseInt(e.target.value, 10))}
                  className="w-full mt-2"
                />

                <div className="flex gap-3 mt-4">
                  <button
                    onClick={clearPaint}
                    type="button"
                    className="px-4 py-2 rounded-xl bg-[#222246] hover:bg-[#2B2B4A]"
                  >
                    Clear mask
                  </button>
                </div>

                <div className="mt-3 flex items-center gap-2 text-xs text-pink-200 bg-[#221b36] border border-pink-700 rounded-lg px-2 py-2">
                  <AlertCircle size={14} className="text-pink-400" />
                  Tip: Keep edges clean; the model replaces exactly the white region.
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Prompt */}
          {step === 3 && (
            <div className="mt-2">
              <div className="flex items-center mb-2">
                <p className="text-gray-300 flex-1">AI-generated prompt (you can edit).</p>

                <button
                  onClick={regeneratePrompt}
                  className="ml-2 p-2 text-pink-400 hover:text-pink-300 rounded-full bg-[#232336] hover:bg-[#2c2c3e] transition disabled:opacity-60"
                  disabled={aiLoading || generating || !selectedImage || !hasPaint}
                  aria-label="Regenerate Prompt"
                  type="button"
                >
                  <RefreshCcw size={20} className={aiLoading ? "animate-spin" : ""} />
                </button>
              </div>

              {aiError && <div className="mb-2 text-red-500 font-semibold">{aiError}</div>}

              <textarea
                className="w-full min-h-[140px] max-h-72 bg-[#1F1F42] rounded-md p-4 text-white border border-gray-600 focus:border-pink-500 focus:ring-1 focus:ring-pink-600 transition"
                value={aiLoading && !prompt ? "AI is loading the message, please hold..." : prompt}
                onChange={(e) => setPrompt(e.target.value)}
                disabled={aiLoading || generating}
                placeholder='e.g. “Replace with a waffle cone ice cream with rainbow sprinkles, soft studio lighting, shallow depth of field”'
                rows={7}
              />

              <div className="mt-3 flex items-center gap-2 text-xs text-pink-200 bg-[#221b36] border border-pink-700 rounded-lg px-2 py-2">
                <AlertCircle size={14} className="text-pink-400" />
                Tip: Be specific about object, materials, color, lighting, mood, and composition.
              </div>
            </div>
          )}
        </div>

        <StepFooter
          step={step}
          onBack={onBack}
          onNext={onNext}
          onGenerate={handleGenerate}
          canNext={canNext}
          canGenerate={canGenerate}
          generating={generating}
        />
      </div>
    </Dialog>
  );
}
